// react
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// API
import { useGetAlarmGroupsByAccountQuery } from '@/api/recalc/endpoints/alarmGroupsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { SubmitButton } from '@/components/ui/button/SubmitButton';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import AlarmGroupDataGrid from '@/features/alarm-hub/components/alarm-group/AlarmGroupDataGrid';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useAlertProvider } from '@/hooks/useAlertProvider';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

export const Route = createFileRoute('/$accountId/_user/alarms/alarm-groups/')(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  const selectedAccount = useSelector(getSelectedAccount);
  const { t } = useTranslation('alarms');

  const { session } = useSessionAuth();

  const { data: alarmGroups = [], isLoading: isAlarmGroupsLoading } =
    useGetAlarmGroupsByAccountQuery(selectedAccount?.AccountId, {
      skip: !selectedAccount?.AccountId,
    });

  const isUserSubscribed =
    !session?.claims?.sub || alarmGroups.length === 0
      ? true
      : alarmGroups.some((group) =>
          group.AlarmSubscriptions?.some(
            (subscription) =>
              subscription.User?.UserId === Number(session.claims.sub),
          ),
        );

  useAlertProvider({
    enabled: !isUserSubscribed,
    severity: 'info',
    message: t('alarmGroups.notSubscribed', { ns: 'alarms' }),
  });

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <GatewayLoadingIndicator isLoading={isAlarmGroupsLoading} />
      <PageHeader title={t('alarmGroups.title', { ns: 'alarms' })} disableArrow>
        <SubmitButton
          component={Link}
          to="create"
          startIcon={<AddRoundedIcon />}
        >
          {t('toolbar.new_alarm_group', {
            defaultValue: 'New',
          })}
        </SubmitButton>
      </PageHeader>

      <AlarmGroupDataGrid
        alarmGroups={alarmGroups}
        userOptions={selectedAccount?.UserRoles}
      />
    </PageContainer>
  );
}
